import React, { FC, useEffect, useState } from 'react';
import { Link, useMatch, useResolvedPath } from 'react-router-dom';
import { Typography } from 'design_system/src';

// css
import './sidebar.scss';

// imgs - icons
import { ReactComponent as Menu } from 'design_system/src/static/icons/menu.svg';
import { ReactComponent as CreditCard } from 'design_system/src/static/icons/credit-card.svg';
import { ReactComponent as Document } from 'design_system/src/static/icons/document.svg';
import { ReactComponent as Repossession } from 'design_system/src/static/icons/compare.svg';
import { ReactComponent as Soli } from 'design_system/src/static/icons/bulleted-list-2.svg';
import { ReactComponent as ChatSquare } from 'design_system/src/static/icons/chat-square.svg';
import { ReactComponent as ShieldGame } from 'design_system/src/static/icons/shield-game.svg';
import { ReactComponent as LeadsIcon } from 'design_system/src/static/icons/target.svg';
import { ReactComponent as GPS } from 'design_system/src/static/icons/gps.svg';
import { ReactComponent as Inventario } from 'design_system/src/static/icons/motorcycle.svg';
import { ReactComponent as Ozocio } from 'design_system/src/static/icons/idea.svg';
import { ReactComponent as Panda } from 'design_system/src/static/icons/panda.svg';
import { ReactComponent as Left } from 'design_system/src/static/icons/left-lg.svg';
import { ReactComponent as Right } from 'design_system/src/static/icons/right-lg.svg';
import { ReactComponent as Maps } from 'design_system/src/static/icons/mesh-fill.svg';
import { getUser } from 'localStorage/authStorage';
import {
	clearInventoryFilter,
	clearOzonerFilter,
	clearOzonioFilter,
	clearSolicitudesFilter,
	clearCreditsFilter,
	clearRepossessionsFilter,
	clearNotificationsFilter,
	clearDevicesFilter,
	clearOzocioFilter,
	clearWriteOffFilter,
	clearExternalInventoryFilter,
} from 'localStorage/filterStorage';
import { Roles, rolesExcluding } from 'models/ozonio/enums/ozonio.enum';
import { ProfileResponse } from 'models/auth/interfaces/auth.interface';
import { CompanyRole } from 'models/companies/enums/companies.enums';

type Props = {
	to: string;
	name: string;
	Img: React.FunctionComponent<
		React.SVGProps<SVGSVGElement> & {
			title?: string | undefined;
		}
	>;
};

export function RequireRole({
	children,
	authRoles,
}: {
	children: JSX.Element;
	authRoles: Roles[] | undefined;
}) {
	const user = getUser();
	if (
		authRoles &&
		(!user || !authRoles.some((role) => user.role.includes(role)))
	) {
		return null;
	}
	return children;
}

const externalNav = [
	{
		to: '/external/vehicles',
		img: Inventario,
		name: 'Vehiculos',
		auth: [],
	},
	{
		to: '/external/gps-map',
		img: Maps,
		name: 'Mapa',
		auth: [],
	},
];

const ozonNav = [
	{
		to: '/',
		img: Menu,
		name: 'Dashboard',
	},
	{
		to: '/loans',
		img: CreditCard,
		name: 'Créditos',
	},
	{
		to: '/sku',
		img: Document,
		name: 'SKU',
		auth: [Roles.ADMIN, Roles.INVENTORY_MODEL_MANAGER],
	},
	{
		to: '/inventory',
		img: Inventario,
		name: 'Inventario',
		auth: rolesExcluding(Roles.SELLER),
	},
	{
		to: '/devices',
		img: GPS,
		name: 'Dispositivos',
		auth: [Roles.ADMIN, Roles.DEVICES_MANAGER],
	},
	{
		to: '/solicitudes',
		img: Soli,
		name: 'Solicitudes',
	},
	{
		to: '/conversaciones',
		img: ChatSquare,
		name: 'Conversaciones',
	},
	{
		to: '/listas-blancas',
		img: ShieldGame,
		name: 'Listas blancas',
		auth: rolesExcluding(Roles.SELLER),
	},
	{
		to: '/leads',
		img: LeadsIcon,
		name: 'Leads',
		auth: [Roles.ADMIN, Roles.CREDIT_ANALYST, Roles.SELLER],
	},
	// {
	// 	to: '/ozocio',
	// 	img: Ozocio,
	// 	name: 'Ozocios',
	// 	auth: [Roles.ADMIN, Roles.BUYER_MANAGER],
	// },
	{
		to: '/recovery',
		img: Repossession,
		name: 'Recuperaciones',

		auth: [Roles.ADMIN, Roles.RECUPERATOR_MANAGER],
	},
	{
		to: '/gps-map',
		img: Maps,
		name: 'Mapa GPS',
		auth: [Roles.ADMIN, Roles.GPS_VIEWER, Roles.GPS_MANAGER],
	},
	{
		to: '/ozonio',
		img: Panda,
		name: 'Ozonios',
		auth: [Roles.ADMIN, Roles.USER_MANAGMENT],
	},
];

const CustomLink: FC<Props> = ({ to, name, Img }) => {
	const resolved = useResolvedPath(to);
	const match = useMatch({ path: `${resolved.pathname}/*`, end: true });

	const cleanUp = () => {
		if (!match) {
			clearInventoryFilter();
			clearExternalInventoryFilter();
			clearOzonerFilter();
			clearOzocioFilter();
			clearOzonioFilter();
			clearSolicitudesFilter();
			clearCreditsFilter();
			clearRepossessionsFilter();
			clearNotificationsFilter();
			clearDevicesFilter();
			clearWriteOffFilter();
		}
	};
	return (
		<div className="m_b_md">
			<Link to={to} className="text_decoration_none" onClick={cleanUp}>
				<div className={`containerSelect ${match && 'active '}`}>
					<Img className="link_icon dim_lg text_neutral_900 " />
					<Typography
						className="link_text text_neutral_900"
						scale="small"
						weight="600"
						component="span"
					>
						{name}
					</Typography>
				</div>
			</Link>
		</div>
	);
};

function Sidebar() {
	const [user, setUser] = useState<ProfileResponse | null>(null);
	const [close, setclose] = useState(true);

	useEffect(() => {
		setUser(getUser());
	}, []);

	const handleSidebar = () => {
		setclose(!close);
	};
	return (
		<div
			className={`sidebar bg_neutral_0 shadow_hard p_y_xl p_x_sm ${
				close ? 'close' : ''
			}`}
		>
			{(user?.company?.role === CompanyRole.ADMIN ? ozonNav : externalNav)
				.filter(
					(ele) =>
						!ele.auth?.length ||
						ele.auth.some((role) => user?.role.includes(role)),
				)
				.map((ele) => (
					<CustomLink
						to={ele.to}
						name={ele.name}
						Img={ele.img}
						key={ele.name}
					/>
				))}
			<div
				className="circle bg_primary_300"
				onClick={handleSidebar}
				onKeyPress={handleSidebar}
				role="button"
				tabIndex={0}
			>
				{close ? <Right className="dim_md" /> : <Left className="dim_md" />}
			</div>
		</div>
	);
}

export default Sidebar;
